import { useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import SecurityDashboardCard from "./SecurityDashboardCard";

export default function SecuritySummaryCards() {
    const { isFeatureEnabled } = useAuth();
    const { call: fetchSummary, response, isLoading } = useBluelagoon({ method: "getSecuritySummary" });

    useEffect(() => { fetchSummary({}); }, [fetchSummary]);

    const data = response?.data;
    const hasSso = isFeatureEnabled("sso");
    const hasAudit = isFeatureEnabled("auditLogs");

    if (isLoading && !data) return null;

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <SecurityDashboardCard
                title="Audit Logs" icon={"\u{1F4CB}"}
                metric={hasAudit ? data?.auditLogs?.count24h : "--"}
                metricLabel="Events in last 24h"
                status={hasAudit ? "Active" : "Disabled"}
                statusColor={hasAudit ? "text-green-600" : "text-muted-foreground"}
                linkTo={hasAudit ? "/profile/audit-logs" : undefined}
                isLite={!!data?.auditLogs?.retentionDays}
                upgradeMessage={data?.auditLogs?.retentionDays ? `Limited to ${data.auditLogs.retentionDays}-day history` : undefined}
            />
            <SecurityDashboardCard
                title="Code Scanning" icon={"\u{1F50D}"}
                metric={data?.codeScanning?.totalFindings ?? "--"}
                metricLabel={`Findings across ${data?.codeScanning?.scanCount || 0} scans`}
                status={`${data?.codeScanning?.totalErrors || 0} errors`}
                statusColor="text-amber-600"
                isLite={!!data?.codeScanning?.maxRules}
                upgradeMessage={data?.codeScanning?.maxRules ? `Limited to ${data.codeScanning.maxRules} rules` : undefined}
            />
            <SecurityDashboardCard
                title="SSO" icon={"\u{1F511}"}
                metric={hasSso ? (data?.sso?.enabled ? data?.sso?.userCount : "Off") : "--"}
                metricLabel={hasSso && data?.sso?.enabled ? "SSO users" : hasSso ? "SSO not configured" : "Not available"}
                status={hasSso ? (data?.sso?.enabled ? "Configured" : "Not configured") : "Disabled"}
                statusColor={hasSso && data?.sso?.enabled ? "text-green-600" : "text-muted-foreground"}
                linkTo={hasSso ? "/profile/sso" : undefined}
                isLite={!!data?.sso?.userLimit}
                upgradeMessage={data?.sso?.userLimit ? `Limited to ${data.sso.userLimit} SSO users per workflow` : undefined}
            />
            <SecurityDashboardCard
                title="Data Obfuscation" icon={"\u{1F6E1}\uFE0F"}
                metric={data?.dataObfuscation?.enabled ? "On" : "Off"}
                metricLabel="Field-level masking"
                status={data?.dataObfuscation?.enabled ? "Available" : "Enterprise Plus only"}
                statusColor={data?.dataObfuscation?.enabled ? "text-green-600" : "text-muted-foreground"}
                upgradeMessage={!data?.dataObfuscation?.enabled ? "Upgrade to Enterprise Plus for data obfuscation" : undefined}
            />
        </div>
    );
}
